import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  MessageCircle,
  Search,
  Sparkles,
  Megaphone,
  Calendar,
  TrendingUp,
  Users,
  CheckCircle2,
  ArrowRight,
  DollarSign,
  Zap,
  Phone,
  FileText,
  BarChart3,
  CreditCard,
  Globe,
  Star,
  Building2,
  Shield,
  ChevronDown,
  X,
  Send,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI Business Heroes Data with actual images
const aiBusinessHeroes = [
  {
    id: 1,
    name: "LEXI",
    role: "AI Marketing Hero",
    category: "Marketing & Traffic",
    description: "Deploys your brand across TV, radio, social, and ads. Publishes content and manages your reputation 24/7.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/igr85vqt_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_15_31%20PM.png",
    color: "#EC4899",
    capabilities: ["Social media management", "Content publishing", "Ad deployment", "Brand promotion", "Reputation management"],
  },
  {
    id: 2,
    name: "ATLAS",
    role: "AI Lead Management Hero",
    category: "Leads & Sales",
    description: "Qualifies leads, manages your pipeline, and tracks every conversation to help close more deals.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/kppw0exm_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_24_54%20PM.png",
    color: "#00E5FF",
    capabilities: ["Lead qualification", "Pipeline management", "Conversation tracking", "Deal closing", "Follow-up automation"],
  },
  {
    id: 3,
    name: "CALLIE",
    role: "AI Appointment Hero",
    category: "Communications",
    description: "Books appointments, confirms schedules, sends reminders, and syncs with your calendar automatically.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/hc05rg8u_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_28_26%20PM.png",
    color: "#F59E0B",
    capabilities: ["Appointment booking", "Calendar sync", "Reminders", "Schedule management", "Confirmation calls"],
  },
  {
    id: 4,
    name: "EMBER",
    role: "AI Automations Hero",
    category: "Operations",
    description: "Manages workflows, automates tasks, and keeps your operations running smoothly around the clock.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/912e00d4_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_33_58%20PM.png",
    color: "#10B981",
    capabilities: ["Workflow automation", "Task management", "Process optimization", "Operations control", "Quality assurance"],
  },
  {
    id: 5,
    name: "REED",
    role: "AI Reporting Hero",
    category: "Data & Reporting",
    description: "Generates daily, weekly, monthly reports. Tracks ROI and performance to optimize your business automatically.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/gyv10f2w_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_36_35%20PM.png",
    color: "#14B8A6",
    capabilities: ["Performance tracking", "ROI analysis", "Automated reports", "Data visualization", "Business intelligence"],
  },
  {
    id: 6,
    name: "KAYA",
    role: "AI Billing Hero",
    category: "Payments & Finance",
    description: "Manages invoicing, payments, subscriptions, and keeps your financial operations running smoothly.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/p9fes7dw_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_02_50%20PM.png",
    color: "#8B5CF6",
    capabilities: ["Invoice management", "Payment processing", "Subscription handling", "Financial tracking", "Revenue optimization"],
  },
  {
    id: 7,
    name: "LI",
    role: "AI Projects Hero",
    category: "Operations",
    description: "Manages projects, coordinates teams, and ensures on-time delivery for your business initiatives.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/22hm5moj_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_06_01%20PM.png",
    color: "#6366F1",
    capabilities: ["Project management", "Team coordination", "Timeline tracking", "Resource planning", "Delivery optimization"],
  },
  {
    id: 8,
    name: "ZANE",
    role: "AI CRM Hero",
    category: "AI Horse-Powered CRM",
    description: "Manages your contacts, deals, pipeline, calendar, and all CRM operations powered by AI.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/34mua9nn_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_07_40%20PM.png",
    color: "#00E5FF",
    capabilities: ["Contact management", "Deal tracking", "Pipeline optimization", "AI-powered insights", "CRM automation"],
  },
  {
    id: 9,
    name: "LAYLA",
    role: "AI Follow Up Hero",
    category: "Leads & Sales",
    description: "Never miss a follow-up again. Tracks missed calls, sends reminders, and prevents lost deals.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/mg8h2yt1_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_04_03%20PM.png",
    color: "#EF4444",
    capabilities: ["Follow-up automation", "Missed call tracking", "Deal recovery", "Reminder systems", "Opportunity rescue"],
  },
  {
    id: 10,
    name: "ARJUN",
    role: "AI Scheduling Hero",
    category: "Communications",
    description: "Manages and optimizes your entire schedule. Resolves conflicts and keeps your calendar organized automatically.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/b4n2p2od_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_05_17%20PM.png",
    color: "#F97316",
    capabilities: ["Smart scheduling", "Conflict resolution", "Calendar optimization", "Meeting coordination", "Time management"],
  },
  {
    id: 11,
    name: "ANIKA",
    role: "AI Email Hero",
    category: "Communications",
    description: "Manages your inbox, prioritizes emails, drafts responses, and keeps your communication flowing.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/j6e3a7uu_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_07_36%20PM.png",
    color: "#3B82F6",
    capabilities: ["Email prioritization", "Inbox management", "Response drafting", "Communication flow", "Email automation"],
  },
  {
    id: 12,
    name: "RAY",
    role: "AI Writer Hero",
    category: "Marketing & Traffic",
    description: "Creates compelling content for social media, blogs, and marketing. Optimizes for readability and engagement.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/8pi2egum_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_09_23%20PM.png",
    color: "#A855F7",
    capabilities: ["Content creation", "Social media writing", "Blog posts", "Readability optimization", "SEO content"],
  },
  {
    id: 13,
    name: "STEPHANIE",
    role: "AI Pipeline Hero",
    category: "Leads & Sales",
    description: "Visualizes and manages your sales pipeline. Tracks prospects, meetings, and won deals in real-time.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/m2hnc8tk_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_18_21%20PM.png",
    color: "#EF4444",
    capabilities: ["Pipeline visualization", "Deal tracking", "Prospect management", "Sales analytics", "Revenue forecasting"],
  },
  {
    id: 14,
    name: "DAISY",
    role: "AI Directory Hero",
    category: "Directory & Network",
    description: "Your AI Directory Assistant. Helps discover top talent, navigate listings, and connect with the best businesses.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/ax9b6bmu_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_39_31%20PM.png",
    color: "#F59E0B",
    capabilities: ["Directory navigation", "Talent discovery", "Business matching", "Best of Best curation", "Network connections"],
  },
  {
    id: 15,
    name: "MAVS",
    role: "AI Prospecting Hero",
    category: "Leads & Sales",
    description: "Identifies and qualifies top prospects. Analyzes data to find ideal leads and rates them for maximum conversion.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/ofc5ri1i_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_44_36%20PM.png",
    color: "#EF4444",
    capabilities: ["Prospect identification", "Lead scoring", "Data analysis", "Conversion optimization", "Sales intelligence"],
  },
  {
    id: 16,
    name: "SARAH",
    role: "AI Reviews Hero",
    category: "Reputation & Community",
    description: "Manages your online reviews and reputation. Collects 5-star feedback and showcases your best testimonials.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/rts6hr1z_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_46_45%20PM.png",
    color: "#F59E0B",
    capabilities: ["Review management", "Reputation monitoring", "Testimonial collection", "Star rating optimization", "Feedback analysis"],
  },
  {
    id: 17,
    name: "CHAD",
    role: "AI Spokesperson Hero",
    category: "Directory & Network",
    description: "Your brand's AI voice. Presents your business to the world and promotes your top offerings across channels.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/dufxsy6a_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_51_56%20PM.png",
    color: "#3B82F6",
    capabilities: ["Brand presentation", "Public speaking", "Business promotion", "Channel distribution", "Media relations"],
  },
  {
    id: 18,
    name: "JUSTIN",
    role: "AI Affiliate Hero",
    category: "Directory & Network",
    description: "Manages your affiliate network. Tracks referrals, builds partnerships, and drives revenue through collaboration.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/200ldo83_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_54_24%20PM.png",
    color: "#F97316",
    capabilities: ["Affiliate management", "Referral tracking", "Partnership building", "Revenue collaboration", "Network growth"],
  },
  {
    id: 19,
    name: "PRIYA",
    role: "AI Content Hero",
    category: "Marketing & Traffic",
    description: "Creates and distributes compelling content. Drives engagement through training, software insights, and coaching.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/3joqda7j_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_01_36%20PM.png",
    color: "#A855F7",
    capabilities: ["Content creation", "Training content", "Engagement driving", "Software insights", "Coaching materials"],
  },
  {
    id: 20,
    name: "CHANCE",
    role: "AI Funding Hero",
    category: "Payments & Finance",
    description: "Helps secure business funding and financial opportunities. Guides you through Co-Op funding and growth capital.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/d907nn6c_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_05_05%20PM.png",
    color: "#10B981",
    capabilities: ["Funding guidance", "Co-Op funding", "Financial planning", "Growth capital", "Investment support"],
  },
  {
    id: 21,
    name: "JULIE",
    role: "AI Course Creator Hero",
    category: "Marketing & Traffic",
    description: "Creates engaging training courses and educational content. Powers your knowledge base and team development.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/58fuhm54_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_24_59%20PM.png",
    color: "#F59E0B",
    capabilities: ["Course creation", "Training development", "Knowledge base", "Team education", "Learning management"],
  },
  {
    id: 22,
    name: "RYAN",
    role: "AI Recruiting Hero",
    category: "Human Capital",
    description: "Streamlines your hiring process. Reviews applicants, identifies top candidates, and manages recruitment pipeline.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/inzqjre6_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_19_21%20PM.png",
    color: "#3B82F6",
    capabilities: ["Applicant screening", "Candidate matching", "Recruitment pipeline", "Hiring automation", "Talent acquisition"],
  },
  {
    id: 31,
    name: "KAITLYN",
    role: "AI Web Designer Hero",
    category: "Technology & Development",
    description: "Creates stunning, conversion-focused websites. Designs beautiful landing pages, sales funnels, and brand experiences that drive results.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/wp4raehf_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_53_38%20PM.png",
    color: "#EC4899",
    capabilities: ["Web design", "Landing pages", "Sales funnels", "Brand design", "UI/UX"],
  },
  {
    id: 32,
    name: "ARJUN",
    role: "AI App Developer Hero",
    category: "Technology & Development",
    description: "Builds powerful mobile and web applications. Turns your business ideas into reality with AI-powered development and automation.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/buyc9tyh_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_56_45%20PM.png",
    color: "#8B5CF6",
    capabilities: ["App development", "Custom software", "API integrations", "Automation", "AI solutions"],
  },
];

// Managed Ad Services Heroes - Managed Services covered with Co-Op Funding
const podAdsHeroes = [
  {
    id: 23,
    name: "ANGELA",
    role: "AI Meta Ad Hero",
    category: "Managed Ad Services",
    description: "Masters Facebook & Instagram advertising. Creates scroll-stopping ads that convert with AI-powered targeting and optimization.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/53wmd6xr_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_49_52%20PM.png",
    color: "#1877F2",
    capabilities: ["Facebook Ads", "Instagram Ads", "Audience targeting", "Ad optimization", "Retargeting"],
  },
  {
    id: 24,
    name: "LOGAN",
    role: "AI Google Ad Hero",
    category: "Managed Ad Services",
    description: "Dominates Google Search and Display. Puts your business at the top when customers are searching for your services.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/3mj9m55a_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_52_30%20PM.png",
    color: "#4285F4",
    capabilities: ["Search Ads", "Display Ads", "YouTube Ads", "Local Service Ads", "Performance Max"],
  },
  {
    id: 25,
    name: "ASHLEY",
    role: "AI TikTok Ad Hero",
    category: "Managed Ad Services",
    description: "Creates viral-worthy TikTok campaigns. Reaches younger audiences with engaging short-form video ads that drive action.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/0190sdtb_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_59_41%20PM.png",
    color: "#00F2EA",
    capabilities: ["TikTok Ads", "Video creation", "Viral campaigns", "Gen Z targeting", "Trend riding"],
  },
  {
    id: 26,
    name: "DEREK",
    role: "AI SEO & Ranking Hero",
    category: "Managed Ad Services",
    description: "Boosts your organic search rankings. Gets your business found on Google without paying for every click.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/oyhcsmgy_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_03_30%20PM.png",
    color: "#10B981",
    capabilities: ["On-page SEO", "Local SEO", "Content optimization", "Link building", "Rank tracking"],
  },
  {
    id: 27,
    name: "MARCUS",
    role: "AI Direct Mail Hero",
    category: "Managed Ad Services",
    description: "Delivers high-impact direct mail campaigns. Reaches customers in their mailbox with personalized, AI-optimized mailers.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/acz2nyhq_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_19_28%20PM.png",
    color: "#F59E0B",
    capabilities: ["Direct mail design", "Targeted mailing lists", "EDDM campaigns", "Postcard marketing", "Response tracking"],
  },
  {
    id: 28,
    name: "JAMES",
    role: "AI TV Ad Hero",
    category: "Managed Ad Services",
    description: "Places your business on 500+ TV stations. Retargets audiences across streaming and broadcast with AI-powered precision.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/v2f8s9jq_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_21_51%20PM.png",
    color: "#EF4444",
    capabilities: ["TV Advertising", "500+ Stations", "Streaming Ads", "Retargeting audiences", "Brand awareness"],
  },
  {
    id: 29,
    name: "DANIELLE",
    role: "AI Radio Ad Hero",
    category: "Managed Ad Services",
    description: "Broadcasts your message across 1000+ radio stations. AI-targeted radio campaigns that reach the right listeners.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/frlt1sun_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_23_55%20PM.png",
    color: "#A855F7",
    capabilities: ["Radio Advertising", "1000+ Stations", "AI Targeting", "Audio campaigns", "Drive-time slots"],
  },
  {
    id: 30,
    name: "BLAKE",
    role: "AI Cinematic Ad Hero",
    category: "Managed Ad Services",
    description: "Creates Hollywood-quality cinematic video ads. AI-powered scripts, stunning visuals, and professional production.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/urc7n4b4_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_47_01%20PM.png",
    color: "#3B82F6",
    capabilities: ["Cinematic Ads", "AI Scripts", "Video production", "Brand storytelling", "Commercial quality"],
  },
];

const heroTeamVideo = "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/392dlkkk_kling_20260205_Image_to_Video_Epic_cinem_123_0.mp4";

// Full Hero System Categories
const heroSystemCategories = [
  {
    category: "System & Platform Intelligence",
    icon: Shield,
    color: "#7C3AED",
    description: "Controls and understands the platform",
    heroes: ["AI Business Hero (Master)", "AI System Control Hero", "AI Agents Management Hero", "AI Product Knowledge Hero", "AI Knowledge Base Hero", "AI Training Hero", "AI Course Creator Hero", "AI Compliance Hero", "AI Security Hero"],
  },
  {
    category: "Communications & Conversations",
    icon: Phone,
    color: "#00E5FF",
    description: "Voice, SMS, email, inbox, chat",
    heroes: ["AI Phone System Hero", "AI Communications Hero", "AI Live Chat Hero", "AI Voice Agent Hero", "AI Call Analysis Hero", "AI Notification Hero", "AI Inbox Hero"],
  },
  {
    category: "Marketing & Traffic",
    icon: Megaphone,
    color: "#EC4899",
    description: "Top-of-funnel growth",
    heroes: ["AI Marketing Hero ✓", "AI Ads Hero", "AI Funnel Hero", "AI SEO Hero", "AI Local Marketing Hero", "AI Retargeting Hero", "AI Branding Hero", "AI Video Hero", "AI Webinar Hero", "AI Social Media Hero", "AI Content Hero ✓"],
  },
  {
    category: "Leads & Sales",
    icon: TrendingUp,
    color: "#F59E0B",
    description: "Revenue engine",
    heroes: ["AI Prospecting Hero ✓", "AI Lead Management Hero ✓", "AI Qualification Hero", "AI Pipeline Hero", "AI Follow Up Hero ✓", "AI Appointment Hero ✓", "AI Sales Hero", "AI Proposal Hero", "AI Upsell Hero"],
  },
  {
    category: "Payments & Finance",
    icon: CreditCard,
    color: "#10B981",
    description: "Money movement",
    heroes: ["AI Billing Hero ✓", "AI Invoicing Hero", "AI Payments Hero", "AI Subscription Hero", "AI Refunds Hero", "AI Revenue Tracking Hero", "AI Business Funding Hero ✓", "AI Co-Op Funding Hero"],
  },
  {
    category: "Operations",
    icon: Building2,
    color: "#6366F1",
    description: "Your fulfillment engine",
    heroes: ["AI Ordering Hero", "AI Fulfillment Hero", "AI Workflow Hero", "AI Automations Hero", "AI Task Hero", "AI Project Hero", "AI Quality Control Hero", "AI SLA Hero"],
  },
  {
    category: "Directory, Affiliate & Network",
    icon: Globe,
    color: "#8B5CF6",
    description: "Your marketplace & distribution",
    heroes: ["AI Directory Hero ✓", "AI Spokesperson Hero ✓", "AI Affiliate Hero ✓", "AI Partner Hero", "AI Referral Hero", "AI Ambassador Hero", "AI Marketplace Hero", "AI Influencer Hero"],
  },
  {
    category: "Reputation & Community",
    icon: Star,
    color: "#EF4444",
    description: "Social proof & loyalty",
    heroes: ["AI Reviews Hero ✓", "AI Reputation Hero", "AI Testimonial Hero", "AI Communities Hero", "AI Social Proof Hero", "AI Loyalty Hero"],
  },
  {
    category: "Data & Reporting",
    icon: BarChart3,
    color: "#14B8A6",
    description: "Intelligence & performance",
    heroes: ["AI Reporting Hero ✓", "AI Analytics Hero", "AI KPI Hero", "AI Forecasting Hero", "AI Attribution Hero", "AI Optimization Hero", "AI A/B Testing Hero"],
  },
  {
    category: "Human Capital",
    icon: Users,
    color: "#F97316",
    description: "Team management",
    heroes: ["AI Human Resources Hero ✓", "AI Recruiting Hero ✓", "AI Onboarding Hero", "AI Performance Hero", "AI Scheduling Hero"],
  },
  {
    category: "AI Horse-Powered CRM Core",
    icon: Bot,
    color: "#00E5FF",
    description: "Explicitly CRM-based functions",
    heroes: ["AI CRM Hero ✓", "AI Contact Management Hero", "AI Deal & Pipeline Hero", "AI Calendar Hero", "AI Email & SMS Hero", "AI Automation Logic Hero", "AI Workflow Builder Hero", "AI Data Management Hero", "AI Permission & Roles Hero"],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Hero Chat Modal Component
function HeroChatModal({ hero, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `hero-${hero?.id}-${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && hero) {
      setMessages([{
        role: "assistant",
        content: `Hi! I'm ${hero.name}, the ${hero.role}. I specialize in ${hero.capabilities.slice(0, 2).join(" and ")}. How can I help your business today?`
      }]);
    }
  }, [isOpen, hero]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/hero`, {
        message: userMessage,
        hero_name: hero.name,
        hero_role: hero.role,
        hero_capabilities: hero.capabilities,
        session_id: sessionId,
      });

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I'm having trouble connecting right now. Please try again in a moment!" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen || !hero) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg h-[600px] glass-card rounded-2xl flex flex-col overflow-hidden border border-white/10"
          style={{ boxShadow: `0 0 60px ${hero.color}30` }}
        >
          {/* Header */}
          <div 
            className="p-4 flex items-center gap-4 border-b border-white/10"
            style={{ background: `linear-gradient(135deg, ${hero.color}20, transparent)` }}
          >
            <img
              src={hero.image}
              alt={hero.name}
              className="w-14 h-14 rounded-full object-cover border-2"
              style={{ borderColor: hero.color }}
            />
            <div className="flex-1">
              <h3 className="text-white font-bold font-['Outfit'] text-lg">{hero.name}</h3>
              <p className="text-sm" style={{ color: hero.color }}>{hero.role}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="hero-chat-close"
            >
              <X className="w-5 h-5 text-[#94A3B8]" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#00E5FF]/20 text-white rounded-br-sm"
                      : "bg-white/10 text-[#E2E8F0] rounded-bl-sm"
                  }`}
                >
                  {msg.content}
                </div>
              </motion.div>
            ))}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-white/10 p-3 rounded-2xl rounded-bl-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: hero.color }} />
                  <span className="text-[#94A3B8] text-sm">{hero.name} is typing...</span>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* DFY/DWY/DIY Action Buttons */}
          <div className="px-4 py-3 border-t border-white/5 bg-gradient-to-r from-[#7C3AED]/10 to-[#00E5FF]/10">
            <p className="text-[#94A3B8] text-xs text-center mb-2">How would you like to proceed?</p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setInputValue("I want this done for me - DFY (Done-For-You)");
                  setTimeout(() => sendMessage(), 100);
                }}
                className="flex-1 px-3 py-2 rounded-lg bg-[#10B981]/20 text-[#10B981] text-xs font-medium hover:bg-[#10B981]/30 transition-colors border border-[#10B981]/30"
              >
                Do this for me (DFY)
              </button>
              <button
                onClick={() => {
                  setInputValue("I want to do this with your team - DWY (Do-With-You)");
                  setTimeout(() => sendMessage(), 100);
                }}
                className="flex-1 px-3 py-2 rounded-lg bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-medium hover:bg-[#F59E0B]/30 transition-colors border border-[#F59E0B]/30"
              >
                Do with me (DWY)
              </button>
              <button
                onClick={() => {
                  setInputValue("I want to do this service myself - DIY");
                  setTimeout(() => sendMessage(), 100);
                }}
                className="flex-1 px-3 py-2 rounded-lg bg-[#00E5FF]/20 text-[#00E5FF] text-xs font-medium hover:bg-[#00E5FF]/30 transition-colors border border-[#00E5FF]/30"
              >
                I'll do it (DIY)
              </button>
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10 bg-white/5">
            <div className="flex items-center gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Ask ${hero.name} anything...`}
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                disabled={isLoading}
                data-testid="hero-chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="px-4"
                style={{ backgroundColor: hero.color }}
                data-testid="hero-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function AIBusinessHeroesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedHero, setSelectedHero] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [businessCount, setBusinessCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats`);
        setBusinessCount(res.data.businesses || 0);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  const openHeroChat = (hero) => {
    setSelectedHero(hero);
    setIsChatOpen(true);
  };

  const closeHeroChat = () => {
    setIsChatOpen(false);
    setSelectedHero(null);
  };

  const filteredHeroes = aiBusinessHeroes.filter(
    (hero) =>
      hero.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hero.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hero.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="page-container min-h-screen" data-testid="ai-heroes-page">
      {/* Hero Section with Team Image */}
      <section className="py-10 md:py-16 hero-pattern relative overflow-hidden">
        <div className="hero-glow top-1/4 left-1/4 opacity-50" />
        <div className="hero-glow bottom-0 right-1/4 opacity-30" style={{ background: "radial-gradient(circle, rgba(0, 229, 255, 0.1) 0%, transparent 70%)" }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-4">
                <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm text-[#F59E0B]">A Full Team of AI Heroes</span>
              </div>
              
              <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
                Meet the <span className="gradient-text">AI Business Heroes™</span>
              </h1>
              
              {/* Trust Element */}
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#00E5FF]/10 border border-[#10B981]/20 w-fit">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#10B981]/20">
                  <Bot className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{businessCount > 0 ? businessCount.toLocaleString() + '+' : '33,000+'}</p>
                  <p className="text-xs text-[#94A3B8]">AI Business Heroes™ active on our platform</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
              </div>
              
              <p className="text-[#94A3B8] text-lg mb-6">
                When your business joins the Best of Best Directory, you don&apos;t just get listed — 
                you get your own <span className="text-[#00E5FF]">AI Directory Hero</span> (AI Chat Bot & Web Call AI Voice Agent) 
                trained on your business, plus a full team of AI Business Heroes™.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-primary">
                    Get $120K Funding <DollarSign className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-secondary">
                    Visit LocalBest.ai <Zap className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/20 to-[#00E5FF]/20 blur-3xl rounded-full" />
              <video
                src={heroTeamVideo}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster="https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/20kwvbqs_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_18_35%20PM.png"
                className="relative rounded-2xl shadow-2xl w-full cursor-pointer"
                style={{ minHeight: '400px', backgroundColor: '#1a1a2e' }}
                onClick={(e) => {
                  // Toggle mute when user clicks
                  e.target.muted = !e.target.muted;
                }}
                title="Click to toggle sound"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Heroes Showcase */}
      <section className="py-16 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-2">
              Featured AI Business Heroes™
            </h2>
            <p className="text-[#94A3B8]">
              Each Hero specializes in a critical area of your business
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          >
            {aiBusinessHeroes.map((hero) => (
              <motion.div
                key={hero.id}
                variants={itemVariants}
                className="glass-card rounded-2xl overflow-hidden group"
              >
                <div className="relative aspect-square">
                  <img
                    src={hero.image}
                    alt={hero.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div 
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mb-2"
                      style={{ backgroundColor: `${hero.color}30`, color: hero.color }}
                    >
                      {hero.category}
                    </div>
                    <h3 className="text-white font-bold font-['Outfit'] text-2xl">{hero.name}</h3>
                    <p style={{ color: hero.color }} className="text-sm font-medium">{hero.role}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-[#94A3B8] text-sm mb-4">{hero.description}</p>
                  <div className="space-y-1 mb-4">
                    {hero.capabilities.slice(0, 3).map((cap, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-[#00E5FF]" />
                        <span className="text-[#E2E8F0]">{cap}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => openHeroChat(hero)}
                    className="w-full text-sm"
                    style={{ backgroundColor: hero.color }}
                    data-testid={`chat-hero-${hero.id}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat with {hero.name}
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-8">
            <p className="text-[#00E5FF] font-medium">
              32 Featured Heroes — Powered by LocalBest.ai Platform + CRM
            </p>
          </div>
        </div>
      </section>

      {/* Managed Ad Heroes Section */}
      <section className="py-16 bg-gradient-to-b from-[#F59E0B]/5 to-transparent" data-testid="pod-ads-heroes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-6">
              <Megaphone className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B]">Managed Ad Services</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Meet Your <span className="text-[#F59E0B]">AI Horse-Powered Ad Heroes</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Get your ads ordered and launched with managed services — <span className="text-[#10B981] font-semibold">covered by Co-Op funding</span>
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {podAdsHeroes.map((hero) => (
              <motion.div
                key={hero.id}
                variants={itemVariants}
                className="glass-card rounded-2xl overflow-hidden group hover:border-[#F59E0B]/50 transition-all"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={hero.image}
                    alt={hero.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${hero.color}30`, color: hero.color }}
                    >
                      {hero.role.replace('AI ', '').replace(' Hero', '')}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-white font-bold font-['Outfit'] mb-1">{hero.name}</h4>
                  <p className="text-[#94A3B8] text-xs line-clamp-2 mb-3">{hero.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {hero.capabilities.slice(0, 3).map((cap, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8] text-[10px]">
                        {cap}
                      </span>
                    ))}
                  </div>
                  <Button
                    onClick={() => openHeroChat(hero)}
                    size="sm"
                    className="w-full text-xs"
                    style={{ backgroundColor: hero.color }}
                    data-testid={`chat-ad-hero-${hero.id}`}
                  >
                    <MessageCircle className="w-3 h-3 mr-1" />
                    Chat
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-8 py-6">
                Get Co-Op Funded for Ads <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Full Hero System */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-6">
              <Bot className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-sm text-[#7C3AED]">Complete AI Business Hero™ System</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              50+ AI Business Heroes™ Across <span className="gradient-text">11 Categories</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Powered by LocalBest.ai Platform + CRM. For more info visit{" "}
              <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline">localbest.ai</a>
            </p>
          </motion.div>

          {/* Category Cards with Expandable Hero Lists */}
          <div className="space-y-4">
            {heroSystemCategories.map((cat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedCategory(expandedCategory === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${cat.color}20` }}
                    >
                      <cat.icon className="w-6 h-6" style={{ color: cat.color }} />
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold font-['Outfit'] text-lg">{cat.category}</h3>
                      <p className="text-[#94A3B8] text-sm">{cat.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[#00E5FF] text-sm font-medium">{cat.heroes.length} Heroes</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-[#94A3B8] transition-transform ${expandedCategory === idx ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
                
                <AnimatePresence>
                  {expandedCategory === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/5"
                    >
                      <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {cat.heroes.map((hero, i) => (
                          <div 
                            key={i}
                            className={`px-3 py-2 rounded-lg text-sm ${
                              hero.includes("✓") 
                                ? "bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30" 
                                : "bg-white/5 text-[#94A3B8]"
                            }`}
                          >
                            {hero}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meta Heroes */}
      <section className="py-16 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-2">
              Meta / Executive Heroes
            </h2>
            <p className="text-[#94A3B8]">High-level narrative heroes for enterprise-level operations</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["AI Growth Hero", "AI Operations Hero", "AI Revenue Hero", "AI Customer Experience Hero", "AI Enterprise Hero"].map((hero, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-4 rounded-xl text-center border border-[#F59E0B]/30"
              >
                <div className="w-12 h-12 rounded-full bg-[#F59E0B]/20 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <p className="text-white font-medium text-sm">{hero}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 rounded-2xl neon-glow-purple"
          >
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Get Your Full Team of AI Business Heroes™
            </h2>
            <p className="text-xl text-[#E2E8F0] mb-2">
              "When your business joins the Best of Best Directory, you don't just get listed."
            </p>
            <p className="text-xl text-[#00E5FF] mb-8">
              "You get a full team of AI Business Heroes powered by our LocalBest.ai Platform and CRM."
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-lg px-8 py-6">
                  Get $120K Funding <DollarSign className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Link to="/submit">
                <Button variant="outline" className="btn-secondary text-lg px-8 py-6">
                  Create My Free Listing <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero Chat Modal */}
      <HeroChatModal
        hero={selectedHero}
        isOpen={isChatOpen}
        onClose={closeHeroChat}
      />
    </div>
  );
}
